import { FireQuotationData } from "../../../../pages/fire/Quotation/components/types/fire";
import { FireCreateQuotePayload, FireQuotePayload } from "../../../../pages/fire/quote/components/types";
import { PoliceEvent } from "../../../../pages/fire/types/Polices";
import { Api } from "../axios-config";

import {
	AssistanceItem,
	ClientFireSettingsPayload,
	CoverageItemData,
	CoveragerRowUpdate,
	FireQuotationResponse,
	IGroupRisk,
	Insurer,
	InsurerDetail,
} from "./types";

/* ============================================================================
   🔧 TIPAGENS AUXILIARES
=============================================================================*/
interface FireListResponse {
	data: FireQuotationData[];
	total: number;
	page: number;
	limit: number;
	totalPages: number;
}
interface TFireTotalCount {
	data: FireQuotationData[];
	totalCount: number;
}

interface PoliceListResponse {
	data: PoliceEvent[];
	total: number;
	page: number;
	limit: number;
	totalPages: number;
}
interface TPolicesTotalCount {
	data: PoliceEvent[];
	totalCount: number;
}

interface TRiskGroupResponse {
	data: IGroupRisk[];
	total: number;
	page: number;
	limit: number;
	totalPages: number;
}

/* ============================================================================
   ⚠️ ERRO UTIL
=============================================================================*/
function extractMessage(error: any, fallback: string): string {
	return error?.response?.data?.message || error?.message || fallback;
}

/* ============================================================================
   🔥 1) FIRE EVENTS (COTAÇÕES)
=============================================================================*/

const getFireQuotations = async (
	page = 1,
	limit = 10,
	name_like = "",
	status = "",
	clientId = "",
	dataini = "",
	datafim = ""
): Promise<TFireTotalCount> => {
	try {
		const { data } = await Api().get<FireListResponse>(`/fire/events/search`, {
			params: {
				page,
				limit,
				nome_like: name_like || undefined,
				status: status || undefined,
				clientId: clientId || undefined,
				dataini: dataini || undefined,
				datafim: datafim || undefined,
			},
		});

		return {
			data: data.data,
			totalCount: data.total || data.data.length,
		};
	} catch (error: any) {
		console.error("❌ Erro ao buscar eventos de incêndio:", error);
		throw new Error(extractMessage(error, "Erro ao buscar eventos de incêndio."));
	}
};

const getFireQuoteByID = async (id: string): Promise<any> => {
	try {
		const { data } = await Api().get(`/fire/events/${id}`);
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao buscar cotação existente:", error);
		throw new Error(extractMessage(error, "Erro ao buscar cotação existente."));
	}
};

const FireCreateQuotation = async (payload: FireCreateQuotePayload): Promise<any> => {
	try {
		const { data } = await Api().post(`/fire/events`, payload);
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao criar cotação:", error);
		throw new Error(extractMessage(error, "Erro ao criar cotação."));
	}
};

const FireUpdateQuotation = async (id: string, payload: FireCreateQuotePayload): Promise<any> => {
	try {
		const { data } = await Api().put(`/fire/events/${id}`, payload);
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao atualizar cotação:", error);
		throw new Error(extractMessage(error, "Erro ao atualizar cotação."));
	}
};

const FireCancelQuotation = async (id: string, cancel_reason: string): Promise<any> => {
	try {
		const { data } = await Api().patch(`/fire/events/cancel/${id}`, { cancel_reason });
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao cancelar cotação:", error);
		throw new Error(extractMessage(error, "Erro ao cancelar cotação."));
	}
};

const FireContractQuotation = async (id: string): Promise<any> => {
	try {
		const { data } = await Api().post(`/fire/proposals/${id}`, {});
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao contratar cotação:", error);
		throw new Error(extractMessage(error, "Erro ao contratar cotação."));
	}
};

const firePrintQuotation = async (id: string): Promise<{ pdfBase64: string }> => {
	try {
		const { data } = await Api().get(`/fire/events/imprimir/${id}`);

		let base64 = data.pdfBase64 ?? data.base64 ?? data.data ?? data;

		base64 = String(base64)
			.replace("data:application/pdf;base64,", "")
			.replace(/\r?\n|\r/g, "");

		return { pdfBase64: base64 };
	} catch (error: any) {
		console.error("❌ Erro ao imprimir cotação:", error);
		throw new Error(extractMessage(error, "Erro ao imprimir cotação."));
	}
};

/* ============================================================================
   🧮 2) CÁLCULO
=============================================================================*/

const FireQuotation = async (
	payload: FireQuotePayload
): Promise<{ data: FireQuotationResponse }> => {
	try {
		const { data } = await Api().post(`/fire/cotacao`, payload);
		return { data };
	} catch (error: any) {
		console.error("❌ Erro ao buscar cotação:", error);
		throw new Error(extractMessage(error, "Erro ao buscar cotação."));
	}
};

/* ============================================================================
   🛡️ 3) POLICES
=============================================================================*/

const getPolices = async (
	page = 1,
	limit = 10,
	name_like = "",
	status = "",
	clientId = "",
	dataini = "",
	datafim = ""
): Promise<TPolicesTotalCount> => {
	try {
		const normalizedStatus = status === "todos" ? "" : status;

		const { data } = await Api().get<PoliceListResponse>(`/fire/proposals/search`, {
			params: {
				page,
				limit,
				nome_like: name_like || undefined,   // pesquisa por nome
				status: normalizedStatus || undefined,
				clientId: clientId || undefined,
				dataini: dataini || undefined,
				datafim: datafim || undefined,
			},
		});

		if (!Array.isArray(data?.data)) {
			throw new Error("Formato de retorno inesperado da API.");
		}

		return {
			data: data.data,
			totalCount: data.total || data.data.length,
		};

	} catch (error: any) {
		console.error("❌ Erro ao buscar apólices:", error);
		throw new Error(extractMessage(error, "Erro ao buscar apólices."));
	}
};

const getPoliceByID = async (id: string): Promise<any> => {
	try {
		const { data } = await Api().get(`/fire/proposals/find/${id}`);
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao buscar apólice existente:", error);
		throw new Error(extractMessage(error, "Erro ao buscar apólice existente."));
	}
};

const firePolicesPrintTermoAdesao = async (id: string): Promise<{ pdfBase64: string }> => {
	try {
		const { data } = await Api().get(`/fire/proposals/termoadesao/${id}`);

		let base64 = data.pdfBase64 ?? data.base64 ?? data.data ?? data;

		base64 = String(base64)
			.replace("data:application/pdf;base64,", "")
			.replace(/\r?\n|\r/g, "");

		return { pdfBase64: base64 };
	} catch (error: any) {
		console.error("❌ Erro ao imprimir cotação:", error);
		throw new Error(extractMessage(error, "Erro ao imprimir cotação."));
	}
};

const firePolicesPrintContratada = async (id: string): Promise<{ pdfBase64: string }> => {
	try {
		const { data } = await Api().get(`/fire/proposals/contratada/${id}`);

		let base64 = data.pdfBase64 ?? data.base64 ?? data.data ?? data;

		base64 = String(base64)
			.replace("data:application/pdf;base64,", "")
			.replace(/\r?\n|\r/g, "");

		return { pdfBase64: base64 };
	} catch (error: any) {
		console.error("❌ Erro ao imprimir cotação:", error);
		throw new Error(extractMessage(error, "Erro ao imprimir cotação."));
	}
};

const firePolicesPrintApolice = async (id: string): Promise<{ pdfBase64: string }> => {
	try {
		const { data } = await Api().get(`/fire/proposals/apolice/${id}`);

		let base64 = data.pdfBase64 ?? data.base64 ?? data.data ?? data;

		base64 = String(base64)
			.replace("data:application/pdf;base64,", "")
			.replace(/\r?\n|\r/g, "");

		return { pdfBase64: base64 };
	} catch (error: any) {
		console.error("❌ Erro ao imprimir cotação:", error);
		throw new Error(extractMessage(error, "Erro ao imprimir cotação."));
	}
};

const firePolicesPrintEndosso = async (id: string): Promise<{ pdfBase64: string }> => {
	try {
		const { data } = await Api().get(`/fire/proposals/endosso/${id}`);

		let base64 = data.pdfBase64 ?? data.base64 ?? data.data ?? data;

		base64 = String(base64)
			.replace("data:application/pdf;base64,", "")
			.replace(/\r?\n|\r/g, "");

		return { pdfBase64: base64 };
	} catch (error: any) {
		console.error("❌ Erro ao imprimir cotação:", error);
		throw new Error(extractMessage(error, "Erro ao imprimir cotação."));
	}
};

const firePolicesCancel = async (id: string, cancel_reason: string): Promise<any> => {
	try {
		const { data } = await Api().patch(`/fire/proposals/cancel/${id}`, { cancel_reason });
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao cancelar cotação:", error);
		throw new Error(extractMessage(error, "Erro ao cancelar cotação."));
	}
};


/* ============================================================================
   🏢 4) SEGURADORAS
=============================================================================*/

const getAllCompanyById = async (
	id: string,
	page = 1,
	limit = 10
): Promise<{ data: Insurer[]; totalCount: number }> => {
	try {
		const resp = await Api().get(`fire/clients/${id}/seguradoras`, {
			params: { _page: page, _limit: limit },
		});

		let insurers = Array.isArray(resp.data)
			? resp.data
			: resp.data.data ?? [];

		const total = resp.headers?.["x-total-count"];
		return { data: insurers, totalCount: total ? Number(total) : insurers.length };
	} catch (error: any) {
		console.error("❌ Erro ao listar seguradoras:", error);
		throw new Error(extractMessage(error, "Erro ao listar seguradoras."));
	}
};

const getAllCompanyDetailById = async (
	id: string,
	idCompany: string
): Promise<InsurerDetail> => {
	try {
		const { data } = await Api().get(`fire/clients/${id}/seguradoras/${idCompany}`);
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao buscar detalhes da seguradora:", error);
		throw new Error(extractMessage(error, "Erro ao buscar detalhes da seguradora."));
	}
};

/* ============================================================================
   🛠️ 5) ASSISTÊNCIAS E COBERTURAS
=============================================================================*/

const getAllAssistanceById = async (
	clientId: string,
	insuranceCompanyId: string,
	page = 1,
	limit = 10
): Promise<{ data: AssistanceItem[]; totalCount: number }> => {
	try {
		const resp = await Api().get(
			`fire/client-assistances/search/${clientId}/${insuranceCompanyId}`,
			{ params: { _page: page, _limit: limit } }
		);

		const list = Array.isArray(resp.data)
			? resp.data
			: resp.data.data ?? [];

		const total = resp.headers?.["x-total-count"];
		return { data: list, totalCount: total ? Number(total) : list.length };
	} catch (error: any) {
		console.error("❌ Erro ao listar assistências:", error);
		throw new Error(extractMessage(error, "Erro ao listar assistências."));
	}
};

const getAllCoverageById = async (
	clientId: string,
	insuranceCompanyId: string,
	page = 1,
	limit = 10
): Promise<{ data: CoverageItemData[]; totalCount: number }> => {
	try {
		const resp = await Api().get(
			`fire/client-coverages/search/${clientId}/${insuranceCompanyId}`,
			{ params: { _page: page, _limit: limit } }
		);

		const list = Array.isArray(resp.data)
			? resp.data
			: resp.data.data ?? [];

		const total = resp.headers?.["x-total-count"];
		return { data: list, totalCount: total ? Number(total) : list.length };
	} catch (error: any) {
		console.error("❌ Erro ao listar coberturas:", error);
		throw new Error(extractMessage(error, "Erro ao listar coberturas."));
	}
};

const getAllCoverageByOccupation = async (
	clientId: string,
	insuranceCompanyId: string,
	occupationId: string
): Promise<CoverageItemData[]> => {
	try {
		const { data } = await Api().get(
			`fire/client-coverages/${clientId}/${insuranceCompanyId}/${occupationId}`
		);
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao buscar coberturas por ocupação:", error);
		throw new Error(extractMessage(error, "Erro ao buscar coberturas por ocupação."));
	}
};

const getAllAssistanceByOccupation = async (
	clientId: string,
	insuranceCompanyId: string,
	occupationId: string
): Promise<AssistanceItem[]> => {
	try {
		const { data } = await Api().get(
			`fire/client-assistances/${clientId}/${insuranceCompanyId}/${occupationId}`
		);
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao buscar assistências por ocupação:", error);
		throw new Error(extractMessage(error, "Erro ao buscar assistências por ocupação."));
	}
};

/* ============================================================================
   👤 6) CLIENTE
=============================================================================*/

const getAllClientDataById = async (
	clientId: string,
	insuranceCompanyId: string
): Promise<any> => {
	try {
		const { data } = await Api().get(
			`fire/clients/por-seguradora/${insuranceCompanyId}?page=1&limit=10&clientId=${clientId}`
		);
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao buscar dados do cliente:", error);
		throw new Error(extractMessage(error, "Erro ao buscar dados do cliente."));
	}
};

/* ============================================================================
   🧩 7) TOKIO SETTINGS
=============================================================================*/

const getTemplateTokioSettings = async (
	clientId: string,
	insuranceCompanyId: string
): Promise<any> => {
	try {
		const { data } = await Api().get(`/fire/template/${clientId}/${insuranceCompanyId}`);
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao buscar template Tokio:", error);
		throw new Error(extractMessage(error, "Erro ao buscar template Tokio."));
	}
};

const updateClientFireSettings = async (
	payload: ClientFireSettingsPayload
): Promise<any> => {
	try {
		const { data } = await Api().post(`/fire/clients`, payload);
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao atualizar configurações do cliente:", error);
		throw new Error(extractMessage(error, "Erro ao atualizar configurações do cliente."));
	}
};

const updateClientFireCoverages = async (
	id: string,
	payload: CoveragerRowUpdate
): Promise<any> => {
	try {
		const { data } = await Api().put(`/fire/client-coverages/${id}`, payload);
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao atualizar cobertura do cliente:", error);
		throw new Error(extractMessage(error, "Erro ao atualizar cobertura do cliente."));
	}
};

/* ============================================================================
   🔥 8) GRUPO DE RISCO
=============================================================================*/

const getGroupRisk = async (): Promise<TRiskGroupResponse> => {
	try {
		const { data } = await Api().get(`fire/grupo-de-risco`);
		return data;
	} catch (error: any) {
		console.error("❌ Erro ao buscar grupos de risco:", error);
		throw new Error(extractMessage(error, "Erro ao buscar grupos de risco."));
	}
};

/* ============================================================================
   🚀 EXPORTS
=============================================================================*/
export const FireServices = {
	// 🔥 Cotações
	getFireQuotations,
	FireQuotation,
	getFireQuoteByID,
	FireCreateQuotation,
	FireUpdateQuotation,
	FireCancelQuotation,
	FireContractQuotation,
	firePrintQuotation,

	// 🛡️ Polices
	getPolices,
	getPoliceByID,
	firePolicesPrintTermoAdesao,
	firePolicesPrintContratada,
	firePolicesPrintApolice,
	firePolicesPrintEndosso,
	firePolicesCancel,

	// 🏢 Seguradoras
	getAllCompanyById,
	getAllCompanyDetailById,

	// 🔧 Cliente
	getAllClientDataById,

	// 🛠️ Coberturas & Assistências
	getAllCoverageById,
	getAllCoverageByOccupation,
	getAllAssistanceById,
	getAllAssistanceByOccupation,

	// 🧩 Configurações Tokio
	getTemplateTokioSettings,
	updateClientFireSettings,
	updateClientFireCoverages,

	// 🔥 Grupo de risco
	getGroupRisk,
};
